/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { usageCancelStripeSubscription } from "../funcs/usageCancelStripeSubscription.js";
import { usageCreateCheckout } from "../funcs/usageCreateCheckout.js";
import { usageCreateCustomerSession } from "../funcs/usageCreateCustomerSession.js";
import { usageCreateStripeCheckout } from "../funcs/usageCreateStripeCheckout.js";
import { usageCreateStripePortalSession } from "../funcs/usageCreateStripePortalSession.js";
import { usageCreateTopUpCheckout } from "../funcs/usageCreateTopUpCheckout.js";
import { usageGetBillingEmail } from "../funcs/usageGetBillingEmail.js";
import { usageGetInferenceSpendCaps } from "../funcs/usageGetInferenceSpendCaps.js";
import { usageGetMeterUsage } from "../funcs/usageGetMeterUsage.js";
import { usageGetPaygBillingSummary } from "../funcs/usageGetPaygBillingSummary.js";
import { usageGetPeriodUsage } from "../funcs/usageGetPeriodUsage.js";
import { usageGetSpendBreakdown } from "../funcs/usageGetSpendBreakdown.js";
import { usageGetStripeSubscription } from "../funcs/usageGetStripeSubscription.js";
import { usageGetTokensUnderManagement } from "../funcs/usageGetTokensUnderManagement.js";
import { usageGetUsageTiers } from "../funcs/usageGetUsageTiers.js";
import { usageResumeStripeSubscription } from "../funcs/usageResumeStripeSubscription.js";
import { usageSetBillingEmail } from "../funcs/usageSetBillingEmail.js";
import { usageSetBillingMetadata } from "../funcs/usageSetBillingMetadata.js";
import { usageSetSpendCap } from "../funcs/usageSetSpendCap.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { BillingEmail } from "../models/components/billingemail.js";
import { InferenceSpendCap } from "../models/components/inferencespendcap.js";
import { MeterUsageResponse } from "../models/components/meterusageresponse.js";
import { PaygBillingSummary } from "../models/components/paygbillingsummary.js";
import { PeriodUsage } from "../models/components/periodusage.js";
import { SpendBreakdownResponse } from "../models/components/spendbreakdownresponse.js";
import { SpendCap } from "../models/components/spendcap.js";
import { StripeSubscription } from "../models/components/stripesubscription.js";
import { TokensUnderManagement } from "../models/components/tokensundermanagement.js";
import { UsageTiers } from "../models/components/usagetiers.js";
import {
  CancelStripeSubscriptionRequest,
  CancelStripeSubscriptionSecurity,
} from "../models/operations/cancelstripesubscription.js";
import {
  CreateCheckoutRequest,
  CreateCheckoutSecurity,
} from "../models/operations/createcheckout.js";
import {
  CreateCustomerSessionRequest,
  CreateCustomerSessionSecurity,
} from "../models/operations/createcustomersession.js";
import {
  CreateStripeCheckoutRequest,
  CreateStripeCheckoutSecurity,
} from "../models/operations/createstripecheckout.js";
import {
  CreateStripePortalSessionRequest,
  CreateStripePortalSessionSecurity,
} from "../models/operations/createstripeportalsession.js";
import {
  CreateTopUpCheckoutRequest,
  CreateTopUpCheckoutSecurity,
} from "../models/operations/createtopupcheckout.js";
import {
  GetBillingEmailRequest,
  GetBillingEmailSecurity,
} from "../models/operations/getbillingemail.js";
import {
  GetInferenceSpendCapsRequest,
  GetInferenceSpendCapsSecurity,
} from "../models/operations/getinferencespendcaps.js";
import {
  GetMeterUsageRequest,
  GetMeterUsageSecurity,
} from "../models/operations/getmeterusage.js";
import {
  GetPaygBillingSummaryRequest,
  GetPaygBillingSummarySecurity,
} from "../models/operations/getpaygbillingsummary.js";
import {
  GetPeriodUsageRequest,
  GetPeriodUsageSecurity,
} from "../models/operations/getperiodusage.js";
import {
  GetSpendBreakdownRequest,
  GetSpendBreakdownSecurity,
} from "../models/operations/getspendbreakdown.js";
import {
  GetStripeSubscriptionRequest,
  GetStripeSubscriptionSecurity,
} from "../models/operations/getstripesubscription.js";
import {
  GetTokensUnderManagementRequest,
  GetTokensUnderManagementSecurity,
} from "../models/operations/gettokensundermanagement.js";
import {
  ResumeStripeSubscriptionRequest,
  ResumeStripeSubscriptionSecurity,
} from "../models/operations/resumestripesubscription.js";
import {
  SetBillingEmailRequest,
  SetBillingEmailSecurity,
} from "../models/operations/setbillingemail.js";
import {
  SetBillingMetadataRequest,
  SetBillingMetadataSecurity,
} from "../models/operations/setbillingmetadata.js";
import {
  SetSpendCapRequest,
  SetSpendCapSecurity,
} from "../models/operations/setspendcap.js";
import { unwrapAsync } from "../types/fp.js";

export class Usage extends ClientSDK {
  /**
   * cancelStripeSubscription usage
   *
   * @remarks
   * Schedule the organization's Stripe PAYG subscription to cancel at the end of its current period
   */
  async cancelStripeSubscription(
    request?: CancelStripeSubscriptionRequest | undefined,
    security?: CancelStripeSubscriptionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<StripeSubscription> {
    return unwrapAsync(usageCancelStripeSubscription(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createCheckout usage
   *
   * @remarks
   * Create a checkout link for upgrading to the business plan
   */
  async createCheckout(
    request?: CreateCheckoutRequest | undefined,
    security?: CreateCheckoutSecurity | undefined,
    options?: RequestOptions,
  ): Promise<string> {
    return unwrapAsync(usageCreateCheckout(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createCustomerSession usage
   *
   * @remarks
   * Create a customer session for the user
   */
  async createCustomerSession(
    request?: CreateCustomerSessionRequest | undefined,
    security?: CreateCustomerSessionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<string> {
    return unwrapAsync(usageCreateCustomerSession(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createStripeCheckout usage
   *
   * @remarks
   * Create a Stripe Checkout link for starting PAYG billing
   */
  async createStripeCheckout(
    request?: CreateStripeCheckoutRequest | undefined,
    security?: CreateStripeCheckoutSecurity | undefined,
    options?: RequestOptions,
  ): Promise<string> {
    return unwrapAsync(usageCreateStripeCheckout(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createStripePortalSession usage
   *
   * @remarks
   * Create a Stripe customer portal session for the organization's PAYG subscription
   */
  async createStripePortalSession(
    request?: CreateStripePortalSessionRequest | undefined,
    security?: CreateStripePortalSessionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<string> {
    return unwrapAsync(usageCreateStripePortalSession(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createTopUpCheckout usage
   *
   * @remarks
   * Create a checkout link for a one-time credit top-up purchase
   */
  async createTopUpCheckout(
    request?: CreateTopUpCheckoutRequest | undefined,
    security?: CreateTopUpCheckoutSecurity | undefined,
    options?: RequestOptions,
  ): Promise<string> {
    return unwrapAsync(usageCreateTopUpCheckout(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getBillingEmail usage
   *
   * @remarks
   * Get the billing notification email for a PAYG organization
   */
  async getBillingEmail(
    request?: GetBillingEmailRequest | undefined,
    security?: GetBillingEmailSecurity | undefined,
    options?: RequestOptions,
  ): Promise<BillingEmail> {
    return unwrapAsync(usageGetBillingEmail(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getInferenceSpendCaps usage
   *
   * @remarks
   * List current usage and caps for the organization's materialized platform-managed inference keys
   */
  async getInferenceSpendCaps(
    request?: GetInferenceSpendCapsRequest | undefined,
    security?: GetInferenceSpendCapsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Array<InferenceSpendCap>> {
    return unwrapAsync(usageGetInferenceSpendCaps(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getMeterUsage usage
   *
   * @remarks
   * Get incrementally aggregated ordinary meter usage by UTC day over a maximum of three calendar months. Duplicate deliveries count unless prevented by the producer.
   */
  async getMeterUsage(
    request: GetMeterUsageRequest,
    security?: GetMeterUsageSecurity | undefined,
    options?: RequestOptions,
  ): Promise<MeterUsageResponse> {
    return unwrapAsync(usageGetMeterUsage(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getPaygBillingSummary usage
   *
   * @remarks
   * Get exact billable usage and estimated cost for the organization's live paid Stripe service period
   */
  async getPaygBillingSummary(
    request?: GetPaygBillingSummaryRequest | undefined,
    security?: GetPaygBillingSummarySecurity | undefined,
    options?: RequestOptions,
  ): Promise<PaygBillingSummary> {
    return unwrapAsync(usageGetPaygBillingSummary(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getPeriodUsage usage
   *
   * @remarks
   * Get the usage for an organization for a given period
   */
  async getPeriodUsage(
    request?: GetPeriodUsageRequest | undefined,
    security?: GetPeriodUsageSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PeriodUsage> {
    return unwrapAsync(usageGetPeriodUsage(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getSpendBreakdown usage
   *
   * @remarks
   * Report spend availability and estimate PAYG organizations' three metered products at current list prices over a maximum of three calendar months. Other plans return unsupported_plan, empty products, and a zero total without calculating estimates. This is not an actual bill: ordinary summaries count duplicate deliveries unless prevented by the producer and exclude adjustment readings.
   */
  async getSpendBreakdown(
    request?: GetSpendBreakdownRequest | undefined,
    security?: GetSpendBreakdownSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SpendBreakdownResponse> {
    return unwrapAsync(usageGetSpendBreakdown(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getStripeSubscription usage
   *
   * @remarks
   * Get the live lifecycle state of the organization's Stripe PAYG subscription
   */
  async getStripeSubscription(
    request?: GetStripeSubscriptionRequest | undefined,
    security?: GetStripeSubscriptionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<StripeSubscription> {
    return unwrapAsync(usageGetStripeSubscription(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getTokensUnderManagement usage
   *
   * @remarks
   * Get tokens under management for the active billing cycle alongside the contracted terms
   */
  async getTokensUnderManagement(
    request?: GetTokensUnderManagementRequest | undefined,
    security?: GetTokensUnderManagementSecurity | undefined,
    options?: RequestOptions,
  ): Promise<TokensUnderManagement> {
    return unwrapAsync(usageGetTokensUnderManagement(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getUsageTiers usage
   *
   * @remarks
   * Get the usage tiers
   */
  async getUsageTiers(
    options?: RequestOptions,
  ): Promise<UsageTiers> {
    return unwrapAsync(usageGetUsageTiers(
      this,
      options,
    ));
  }

  /**
   * resumeStripeSubscription usage
   *
   * @remarks
   * Remove a scheduled end-of-period cancellation from the organization's Stripe PAYG subscription
   */
  async resumeStripeSubscription(
    request?: ResumeStripeSubscriptionRequest | undefined,
    security?: ResumeStripeSubscriptionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<StripeSubscription> {
    return unwrapAsync(usageResumeStripeSubscription(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setBillingEmail usage
   *
   * @remarks
   * Set or clear the billing notification email for a PAYG organization
   */
  async setBillingEmail(
    request: SetBillingEmailRequest,
    security?: SetBillingEmailSecurity | undefined,
    options?: RequestOptions,
  ): Promise<BillingEmail> {
    return unwrapAsync(usageSetBillingEmail(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setBillingMetadata usage
   *
   * @remarks
   * Set an organization's billing contract terms. Restricted to platform admins.
   */
  async setBillingMetadata(
    request: SetBillingMetadataRequest,
    security?: SetBillingMetadataSecurity | undefined,
    options?: RequestOptions,
  ): Promise<TokensUnderManagement> {
    return unwrapAsync(usageSetBillingMetadata(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setSpendCap usage
   *
   * @remarks
   * Set the monthly spend cap for one of a PAYG organization's platform-managed inference keys
   */
  async setSpendCap(
    request: SetSpendCapRequest,
    security?: SetSpendCapSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SpendCap> {
    return unwrapAsync(usageSetSpendCap(
      this,
      request,
      security,
      options,
    ));
  }
}
